import { jwtDecode } from "@/features/auth/invalid-token-error";
import { jwtPayload } from "@/features/auth/types";
import { setJwt } from "@/features/auth/utils";
import type { ConfirmationResponse } from "@/features/register/types";
import { AuthApiClient } from "@/features/user/api-client/api-client";

import { factory_account_create } from "@/features/user/factories";
import { useUser } from "@/features/user/hooks/use-user";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import useCaptchaVerify from "@/features/utils/hooks/use_captcha_verify";
import type { SignupState } from "@/models/signup";
import { useAppDispatch } from "@/redux/hooks";
import { setExpirationToken, setLinkedCompanies, setRoles } from "@/redux/slices/userSlice";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";

export type UseCreateAccountProps = {
  signupState: SignupState;
  email: string;
  password: string;
  realIdentityUuid?: string;
};

export const useCreateAccount = (
  options?: UseMutationOptions<ConfirmationResponse, Error, UseCreateAccountProps>
) => {
  const { getLanguage } = usei18n();
  const { useCreateProfile } = useUser();

  const dispatch = useAppDispatch();
  const { mutateAsync: createProfile } = useCreateProfile();
  const { handleReCaptchaVerify } = useCaptchaVerify();

  return useMutation<ConfirmationResponse, Error, UseCreateAccountProps>({
    mutationFn: async ({ signupState, password, email }: UseCreateAccountProps) => {
      const staticData = await AuthApiClient.getStaticData("fr");

      const captchaToken = await handleReCaptchaVerify();
      const codeLanguage = getLanguage();
      const accountCreatePayload = factory_account_create(
        email,
        password,
        captchaToken ?? "",
        codeLanguage,
        signupState,
        staticData
      );
      const response = await createProfile(accountCreatePayload);
      const decoded: jwtPayload = jwtDecode(response.token);
      const { exp, roles, linked_companies } = decoded;
      if (roles) dispatch(setRoles({ roles: roles }));
      dispatch(setLinkedCompanies({ linkedCompanies: linked_companies }));
      dispatch(setExpirationToken(Number(exp)));

      setJwt({ token: response.token });
      return { success: true, message: "" };
    },
    ...options
  });
};
